// ds-color-ignore-file: dev-only / internal tooling surface
"use client"

import { useEffect, useRef } from "react"

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * The shared editor-modal chrome: a fixed full-viewport backdrop, a centered
 * white dialog, backdrop-click + Escape dismissal, and the `data-ahead-editor-ui`
 * marker the overlay uses to ignore its own surface. Used by both the
 * locale-grid / CMS scalar modals and the whole-field Lexical body modal — one
 * source of truth for z-index, dismissal, and the capture-phase Escape handler
 * (so it wins over page-level key handlers and there's no duplicate listener).
 *
 * A11y (finding 6): the dialog is `role="dialog" aria-modal="true"` labelled by
 * its human header, focuses its first focusable element on open, traps Tab inside
 * it, and restores focus to whatever was focused before it opened on close.
 */
export function ModalShell({
  children,
  onClose,
  title,
  ariaLabel,
  width,
  maxHeight,
}: {
  children: React.ReactNode
  onClose: () => void
  title?: React.ReactNode
  /** Accessible name for the dialog — the human-readable header. */
  ariaLabel?: string
  /** Dialog width in px. */
  width: number
  /** Dialog max-height (CSS length). */
  maxHeight: string
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener("keydown", onKey, true)
    return () => document.removeEventListener("keydown", onKey, true)
  }, [onClose])

  // Focus management: focus the first focusable on open, trap Tab inside the
  // dialog, restore focus to the previously-focused element on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const dialog = dialogRef.current
    if (dialog) {
      const first = dialog.querySelector<HTMLElement>(FOCUSABLE)
      ;(first ?? dialog).focus()
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Tab" || !dialogRef.current) return
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      )
      if (focusable.length === 0) {
        e.preventDefault()
        return
      }
      const firstEl = focusable[0]
      const lastEl = focusable[focusable.length - 1]
      const activeEl = document.activeElement
      if (e.shiftKey && activeEl === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && activeEl === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }
    document.addEventListener("keydown", onKeyDown, true)
    return () => {
      document.removeEventListener("keydown", onKeyDown, true)
      previouslyFocused?.focus?.()
    }
  }, [])

  return (
    <div
      data-ahead-editor-ui
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483660,
        background: "rgba(8,10,20,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={
          ariaLabel ?? (typeof title === "string" ? title : undefined)
        }
        tabIndex={-1}
        style={{
          width,
          maxWidth: "94vw",
          maxHeight,
          overflow: "auto",
          background: "#fff",
          borderRadius: 16,
          padding: 18,
          fontFamily: "ui-sans-serif, system-ui",
          outline: "none",
        }}
      >
        {title ? (
          typeof title === "string" ? (
            <h3 style={{ fontFamily: "ui-monospace, monospace", fontSize: 14 }}>
              {title}
            </h3>
          ) : (
            title
          )
        ) : null}
        {children}
      </div>
    </div>
  )
}
